import { createContext, useContext } from "react";
import { featureOn, type Role } from "@szerviz/core";
import type { DataStore, MfaRequirement, MfaStatus, MySubscription, Workshop } from "./data";

export interface AppContextValue {
  store: DataStore;
  workshop: Workshop;
  /** Re-reads the workshop (after its settings changed). */
  reloadWorkshop: () => Promise<void>;
  /** Whether the question mark help is switched on for this user. */
  helpEnabled: boolean;
  setHelpEnabled: (on: boolean) => Promise<void>;
  /** The signed in user's role in this workshop (null while loading). */
  role: Role | null;
  /** Moves to a page (and optionally one record on it) through the address hash. */
  go: (page: string, id?: string) => void;
  /** One step back to the previous screen of the program; to `fallback` when there is none. */
  back: (fallback: string) => void;
  /** Two-step sign in of the signed in user (Settings shows and changes it). */
  mfa?: { status: MfaStatus; requirement: MfaRequirement };
  reloadMfa?: () => Promise<void>;
  /** The workshop's subscription (trial, Profi week, plan; null while loading or when it cannot be read). */
  subscription?: MySubscription | null;
  reloadSubscription?: () => Promise<void>;
}

export const AppContext = createContext<AppContextValue | null>(null);

export function useApp(): AppContextValue {
  const v = useContext(AppContext);
  if (!v) throw new Error("AppContext missing");
  return v;
}

/** Help is optional: components outside the shell (login) render without it. */
export function useHelpEnabled(): boolean {
  return useContext(AppContext)?.helpEnabled ?? false;
}

/** Same defaults as public.default_permission_roles() in the database (which enforces them). */
export const DEFAULT_PERMISSIONS: Record<string, Role[]> = {
  "workOrder.reopen": ["owner"],
  "workOrder.delete": ["owner", "manager"],
};
export const PERMISSIONS = Object.keys(DEFAULT_PERMISSIONS);

export function can(role: Role | null, workshop: Workshop, permission: string): boolean {
  if (!role) return false;
  if (role === "owner") return true;
  return (workshop.permissions[permission] ?? DEFAULT_PERMISSIONS[permission] ?? ["owner"]).includes(role);
}

/** A switchable feature works in this workshop: in the package and switched on. */
export function useFeature(key: string): boolean {
  const { workshop } = useApp();
  return featureOn(workshop, key);
}
